import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const features = [
  {
    id: 1,
    icon: "/file-logo.svg",
    image: "/2nd-person-testimonial.svg",
    decorativeImage: "/side-dots.svg",
    title: "AI Proposal Generation",
    description:
      "Create proposals based on job descriptions, skills required, and project scope to keep bids relevant and professional.",
  },
  {
    id: 2,
    icon: "/fileSearch.svg",
    image: "/girl-testimonial.svg",
    decorativeImage: "/side-dots.svg",
    title: "Manual Bidding Control",
    description:
      "Review jobs, edit AI-generated proposals to match your style, and place bids yourself when you want full control over every submission. Perfect for freelancers who value personalization and precision.",
  },
  {
    id: 3,
    icon: "/soundup-icon.svg",
    image: "/1st-person-testimonial.svg",
    decorativeImage: "/side-dots.svg",
    title: "Daily Limits & Safety Controls",
    description:
      "Turn on automated bidding and let Bidzal submit bids on relevant jobs while you work on other tasks.Set bidding limits, keywords, and filters to keep everything under your control.",
  },
];

export default function Features() {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const cardRefs = useRef([]);

  const setCardRef = (el, index) => {
    if (el) {
      cardRefs.current[index] = el;
    }
  };

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const section = sectionRef.current;
    const header = headerRef.current;
    const content = contentRef.current;
    if (!section || !header || !content) return undefined;

    const syncSpacing = () => {
      // Keep a consistent gap under the header.
      content.style.marginTop = "56px";
    };

    syncSpacing();

    // Keep header pinned through all cards; no extra spacing is added by ScrollTrigger.
    const headerTrigger = ScrollTrigger.create({
      trigger: header,
      start: "top top",
      endTrigger: section,
      end: "bottom top",
      pin: true,
      pinSpacing: false,
      anticipatePin: 1,
      onRefresh: syncSpacing,
    });

    const cardTriggers = cardRefs.current.map((card) =>
      ScrollTrigger.create({
        trigger: card,
        // Stick cards at the very top of the viewport (over the header).
        start: "top top",
        end: () => `+=${card.offsetHeight}`,
        pin: true,
        pinSpacing: false,
        anticipatePin: 1,
      })
    );

    const handleResize = () => {
      syncSpacing();
      ScrollTrigger.refresh();
    };

    window.addEventListener("resize", handleResize);
    ScrollTrigger.addEventListener("refreshInit", syncSpacing);

    return () => {
      headerTrigger.kill();
      cardTriggers.forEach((t) => t.kill());
      window.removeEventListener("resize", handleResize);
      ScrollTrigger.removeEventListener("refreshInit", syncSpacing);
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-16 mb-72 md:mb-96 lg:mb-56 bg-white">
      <div className="max-w-none mx-auto px-4 sm:px-6 lg:px-8 2xl:px-32">
        {/* Header */}
        <div ref={headerRef} className="text-center">
          <h2 className="text-4xl 2xl:text-5xl font-gilroy font-extrabold heading-strong text-primary-blue mb-4">
            Features
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
          From AI-powered proposals to automated bidding, streamline your freelance workflow 
          and focus on what matters—delivering great work.
          </p>
        </div>

        {/* Features List */}
        <div
          ref={contentRef}
          className="space-y-8 mx-auto w-full "
        >
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.id}
              feature={feature}
              setCardRef={(el) => setCardRef(el, index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ feature, setCardRef }) {
  return (
    <div
      ref={setCardRef}
        className="relative bg-light-blue-shade-20 border-2 border-primary-blue rounded-3xl p-1 md:p-6 lg:p-2 2xl:p-2 shadow-lg 2xl:min-h-[350px] w-full max-w-[1200px] mx-auto"
    >
      {/* Top Right Decorative Image */}
      <div className="absolute right-0 z-10">
        <img
          src={feature.decorativeImage}
          alt=""
          className="w-12 h-24 lg:w-24 lg:h-28 object-contain"
        />
      </div>

      <div className="flex flex-col lg:flex-row gap-4 md:gap-8 2xl:gap-12 items-center">
        {/* Image Section */}
        <div className="flex-shrink-0">
          <div className="w-full max-w-[300px] md:max-w-[350px] lg:max-w-[460px] 2xl:max-w-[520px] z-20">
            <img
              src={feature.image}
              alt={feature.title}
              className="w-full h-auto 2xl:h-[320px] object-cover"
            />
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 space-y-0 max-md:p-4 max-lg:p-6">
          {/* Icon */}
          <div className=" hidden lg:block relative -left-9 -top-4  2xl:-left-12  z-10 pointer-events-none">
            <img
              src={feature.icon}
              alt=""
              className="w-36 h-36 2xl:w-40 2xl:h-40 object-contain"
            />
          </div>

          {/* Title */}
          <h3 className="relative -top-4  lg:-top-14 text-2xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-bold text-gray-900">
            {feature.title}
          </h3>

          {/* Description */}
          <p className="relative -top-4 lg:-top-12 text-gray-600 text-base lg:text-lg 2xl:text-xl leading-relaxed">
            {feature.description}
          </p>
        </div>
      </div>
    </div>
  );
}
